/**
 * The finding rows an element's form shows: the element's current findings, which are the
 * draft's once one stands and the saved ones until then, and one row per current finding of
 * each other element the draft changes.
 */
import type { InstrumentRef } from '../pnid/entityRef';
import { equipmentTargetLabel, nodeTargetLabel } from '../pnid/targetRailItems';
import type { DraftValidation, ElementKind, Finding, PnidEquipment, PnidNode } from '../types';
import { formatInstrumentIdentity } from '../utils/instrumentIdentity';

/** The drawing's elements as an editor holds them, which name a finding's related ids. */
export interface FindingRelatedElements {
  nodes: Pick<PnidNode, 'id' | 'node_no' | 'line_no'>[];
  equipments: Pick<PnidEquipment, 'id' | 'equipment_no'>[];
  instruments: InstrumentRef[];
}

/** A line or an equipment as the rail spells it, an instrument as the editors' pickers do; null
 *  when the drawing no longer holds the id. */
export function relatedElementLabel(id: string, elements: FindingRelatedElements): string | null {
  const node = elements.nodes.find((candidate) => candidate.id === id);
  if (node) return nodeTargetLabel(node);
  const equipment = elements.equipments.find((candidate) => candidate.id === id);
  if (equipment) return equipmentTargetLabel(equipment);
  const instrument = elements.instruments.find((candidate) => candidate.id === id);
  return instrument ? formatInstrumentIdentity(instrument) : null;
}

/** The element's current findings: the draft's while one stands, else the saved ones. */
export function currentFindings(saved: Finding[], draft: DraftValidation | null): Finding[] {
  return draft ? draft.element.findings : saved;
}

const KIND_LABEL: Record<ElementKind, string> = { node: 'Line', equipment: 'Equipment', instrument: 'Instrument' };

/** One current finding of another element the draft changes. */
export interface ChangedElementFinding {
  key: string;
  finding: Finding;
  /** `Line #24 LG 305 Tapping 2 · LINE-001 · 16 instruments on LG 305 Tapping 2 and none has an order` */
  text: string;
}

/** A row per current finding of each element in `draft.changed`. An element whose findings
 *  all clear has no row. */
export function changedElementFindings(
  draft: DraftValidation | null,
  elements: FindingRelatedElements,
): ChangedElementFinding[] {
  if (!draft) return [];
  return Object.entries(draft.changed).flatMap(([id, { kind, findings }]) => {
    const label = `${KIND_LABEL[kind]} ${relatedElementLabel(id, elements) ?? id}`;
    return findings.map((finding, index) => ({
      key: `${id}-${finding.rule}-${index}`,
      finding,
      text: `${label} · ${finding.rule} · ${finding.message}`,
    }));
  });
}
